import type Commande from "models/Commande";
import { Authorization } from "@sdeapps/react-core";
import type { User } from "@sdeapps/react-core";
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router";
import graphService from "services/graphService";
import commandesService from "services/commandesService";
import type PatchData from "models/PatchData";
import ModalPageTemplate from "components/templates/ModalPageTemplate";
import CommandePageTemplateSkeleton from "components/templates/CommandePageTemplateSkeleton";
import NotFoundErrorPage from "pages/errors/NotFoundErrorPage";
import ApplicationRoles from "constants/ApplicationRoles";
import StoreManagerCommandePageTemplate from "components/templates/StoreManagerCommandePageTemplate";
import { useDispatch } from "react-redux";
import type store from "store/store";
import { getAllCommandes, setLoadingCommandsIds } from "reducers/commandesReducer";
import type CommandeArticleFormModel from "models/CommandeArticleFormModel";
import { routesConfig } from "config/app-config";
import Statut from "constants/Statut";
import { useSnackbar } from "notistack";
import ToastMessages from "constants/ToastMessages";

/**
 * Page réservée aux Magasiniers, listant les commandes en attente de préparation ou traitement
 */
function StoreManagerCommandePage(): ReactNode {
  const { commandeId } = useParams();
  const [commande, setCommande] = useState<Commande>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [commentaire, setCommentaire] = useState<string>("");
  const [articlesList, setArticlesList] = useState<Array<CommandeArticleFormModel>>([]);
  const [demandeur, setDemandeur] = useState<User | undefined>();

  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const dispatch = useDispatch<typeof store.dispatch>();

  useEffect(() => {
    async function getAgent(agentId: string): Promise<void> {
      const fetchedAgents = await graphService.getAgentsByIds([agentId]);
      setDemandeur(fetchedAgents.find((u) => u.id === agentId));
    }

    async function getCommande(): Promise<void> {
      if (commandeId == null) {
        // TODO meilleure gestion des erreurs
        return;
      }
      const fetchedCommande: Commande = await commandesService.getById(commandeId);
      fetchedCommande.articles.sort((a, b) => a.commandeArticleNumber - b.commandeArticleNumber);

      setCommande(fetchedCommande);
      void getAgent(fetchedCommande.demandeurId);

      setArticlesList([...fetchedCommande.articles]);

      setIsLoading(false);
    }

    void getCommande();
  }, [commandeId]);

  async function updateCommande(): Promise<void> {
    if (commandeId == null) {
      // TODO meilleure gestion des erreurs
      return;
    }
    const articlesCommandesSansNumeroDeBonDeCommande = articlesList.filter(
      (a) =>
        a.statutArticle === Statut.COMMANDE &&
        (a.numeroBonDeCommande == null || a.numeroBonDeCommande === "")
    );
    if (articlesCommandesSansNumeroDeBonDeCommande.length > 0) {
      enqueueSnackbar(ToastMessages.NO_BON_DE_COMMANDE, { variant: "error" });
      return;
    }

    setIsSending(true);
    dispatch(setLoadingCommandsIds(commandeId));

    const commandeArticlesPatches: Array<PatchData> = articlesList.reduce<Array<PatchData>>(
      (patches, { commandeArticleNumber, statutArticle, numeroBonDeCommande }) => {
        patches.push({
          op: "replace",
          path: `/articles/${commandeArticleNumber}/statutArticle`,
          value: statutArticle,
        });
        if (statutArticle === Statut.COMMANDE) {
          patches.push({
            op: "replace",
            path: `/articles/${commandeArticleNumber}/numeroBonDeCommande`,
            value: numeroBonDeCommande,
          });
        }
        return patches;
      },
      []
    );

    try {
      if (commandeArticlesPatches.length > 0) {
        if (commentaire.trim().length !== 0) {
          await commandesService.addComment(commandeId, commentaire);
        }
        await commandesService.patchCommande(commandeId, commandeArticlesPatches);
      } else throw new Error();
      // TODO error handling
    } catch (error) {
      // TODO error handling
      console.error(error);
    } finally {
      setIsSending(false);
      void dispatch(getAllCommandes());
    }
    navigate(routesConfig.magasinier.path);
  }

  if (isLoading) {
    return (
      <ModalPageTemplate returnPath={routesConfig.magasinier.path}>
        <CommandePageTemplateSkeleton />
      </ModalPageTemplate>
    );
  }
  if (commande == null || commandeId == null) {
    return (
      <ModalPageTemplate returnPath={routesConfig.magasinier.path}>
        <NotFoundErrorPage />
      </ModalPageTemplate>
    );
  }

  return (
    <ModalPageTemplate returnPath={routesConfig.magasinier.path}>
      <Authorization roles={ApplicationRoles.STOREMANAGER}>
        <StoreManagerCommandePageTemplate
          demandeur={demandeur}
          articlesList={articlesList}
          updateCommande={updateCommande}
          isSending={isSending}
          setArticlesList={setArticlesList}
          commentaire={commentaire}
          setCommentaire={setCommentaire}
          commande={commande}
        />
      </Authorization>
    </ModalPageTemplate>
  );
}

export default StoreManagerCommandePage;
